import type { Query } from '../../query'
import * as edge from '../edge'

import type { Cache } from './cache'
import { Id } from './id'

/**
 * Represents a collection of data meant to be retrieved/used in a
 * {@link Query}
 *
 * @see {@link Node.ForEntity}, {@link Node.ForGroup}, {@link Node.ForValue}
 */
export abstract class Node {
  /** Unique id within the context of a {@link Graph} */
  public readonly id: Id
  public readonly edges = new edge.Tracker(this)

  /**
   * {@link Query} that this {@link Node} is based on
   */
  public readonly query: Query

  /**
   * In {@link traverse}, when traversing connection edges, we need to start
   * somewhere. Typically, we start with this {@link Node}, but some subclasses
   * may override this to start with other nodes
   *
   * @see {@link consumptionTraversalStartingNodes}
   */
  protected readonly connectionTraversalStartingNodes: readonly Node[] = [this]
  /**
   * In {@link traverse}, when traversing consumption edges, we need to start
   * somewhere. Typically, we start with this {@link Node}, but some subclasses
   * may override this to start with other nodes
   *
   * @see {@link connectionTraversalStartingNodes}
   */
  protected readonly consumptionTraversalStartingNodes: readonly Node[] = [this]

  private readonly _nodesCache: Cache

  constructor({
    id = Id.FOR_ROOT,
    nodesCache,
    query,
  }: Readonly<Node.constructor.Params>) {
    this.id = id
    this.query = query
    this._nodesCache = nodesCache
    nodesCache.add(this)
  }

  /**
   * Removes this node from the associated {@link Graph}
   *
   * __WARNING:__ this cannot be undone
   */
  public destroy(): void {
    this.edges.destroy()
    this._nodesCache.remove(this)
  }

  /**
   * TODO: add documentation explaining what are we doing here.
   */
  public traverse<TEdgeListType extends edge.List.Type>(
    params: Readonly<Node.traverse.Params<TEdgeListType>>,
  ): Generator<{
    previousNode: Node
    edge?: edge.From<edge.Tracker[TEdgeListType]>
  }>
  public *traverse(
    params: Readonly<Node.traverse.Params<edge.List.Type<edge.Edge>>>,
  ): Generator<{
    previousNode: Node
    /**
     * Edge that is being traversed, or if this is the initial node, this is
     * `undefined`
     */
    edge?: edge.Edge
  }> {
    const { type, skipVisitedNodes = false, reversed = false } = params

    /**
     * If this node represents a group and we are interested in connection
     * edges, the edges are not going to be to/from this node, but rather its
     * members. Thus, we start at the members. Otherwise, we can just start
     * with this node
     */
    const startingNodes: readonly Node[] =
      type === 'connectedBy' || type === 'connecting'
        ? this.connectionTraversalStartingNodes
        : this.consumptionTraversalStartingNodes

    for (const startingNode of startingNodes) {
      if (!reversed) {
        yield { previousNode: startingNode }
      }
      const visitedNodeIds = new Set<string>([startingNode.id])
      for (const _edge of startingNode.edges[type].edges) {
        // If we are skipping visited nodes, we don't want to traverse
        // a visited note
        if (skipVisitedNodes && visitedNodeIds.has(_edge.node.id)) {
          continue
        }

        if (!reversed) {
          visitedNodeIds.add(_edge.node.id)
          yield { edge: _edge, previousNode: startingNode }
        }

        for (const nested of _edge.node.traverse(params)) {
          if (
            // For nested edges, we just want the edges, not the initial node,
            // since that is covered by the current edge
            !nested.edge ||
            // If we are skipping visited nodes, we don't want to traverse
            // a visited note
            (skipVisitedNodes && visitedNodeIds.has(nested.edge.node.id))
          ) {
            continue
          }
          visitedNodeIds.add(nested.edge.node.id)
          yield nested
        }

        if (reversed) {
          visitedNodeIds.add(_edge.node.id)
          yield { edge: _edge, previousNode: startingNode }
        }
      }

      if (reversed) {
        yield { previousNode: startingNode }
      }
    }
  }
}
export namespace Node {
  export namespace constructor {
    export interface Params {
      /**
       * @default ROOT_NODE_ID
       * @see {@link Node.id}
       */
      id?: Id
      /**
       * Used to register this {@link Node}
       */
      nodesCache: Cache
      /**
       * {@link Query} used to create this {@link Node}
       */
      query: Query
    }
  }

  export namespace traverse {
    export interface Params<TEdgeListType extends edge.List.Type> {
      /**
       * Should go through the edges in reverse order?
       *
       * @default false
       */
      reversed?: boolean
      /**
       * Type of edges to traverse
       */
      type: TEdgeListType
      /**
       * Avoid traversing the same node more than once
       *
       * @default false
       */
      skipVisitedNodes?: boolean
    }
  }
}
